import { zodResolver } from '@hookform/resolvers/zod'
import { addHours } from 'date-fns/addHours'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'

import { useShiftMutations } from '../../hooks/use-shift-mutations'
import type { CreateShiftDialogProps } from '../../types'
import { normalizeShiftMutationData } from '../../utils'
import { CreateEditShiftForm } from '../create-edit-form'
import { ShiftFormSchema, nonRepeatOption } from '../create-edit-form/consts'
import type { ShiftFormValues, Weekday } from '../create-edit-form/types'

const defaultValues: ShiftFormValues = {
  startedAt: new Date(),
  finishedAt: addHours(new Date(), 8),
  memberIds: [],
  creatorIds: [],
  recurring: nonRepeatOption.value,
  days: [] as Weekday[],
  isRepetitionInfinite: 'true',
}

export const CreateShiftDialog = ({
  isModalVisible,
  onClose,
  creators,
}: CreateShiftDialogProps) => {
  const { createRepeatableShift, createSingleShift, isCreating } =
    useShiftMutations()

  const form = useForm<ShiftFormValues>({
    mode: 'all',
    defaultValues,
    resolver: zodResolver(ShiftFormSchema),
  })

  const handleClose = () => {
    onClose()
    form.reset()
  }

  const onSubmit = (values: ShiftFormValues) => {
    const { data, isRepeatable } = normalizeShiftMutationData(values)

    return isRepeatable
      ? createRepeatableShift(data, {
          onSuccess: handleClose,
        })
      : createSingleShift(data, {
          onSuccess: handleClose,
        })
  }

  return (
    <Dialog
      show={isModalVisible}
      onClose={handleClose}
      bigDialog
      panelClassName="sm:my-0 max-h-full overflow-auto"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CreateEditShiftForm
            title="Create Shift"
            onClose={handleClose}
            creators={creators}
            submitButton={
              <Button
                type="submit"
                isLoading={isCreating}
              >
                Save
              </Button>
            }
            isAllowedToChangeRepetition
          />
        </form>
      </FormProvider>
    </Dialog>
  )
}
